import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Buildings, HeartStraight, Brain, ForkKnife, ArrowRight, ArrowUpRight, Star, CheckCircle, Fire, Barbell, Clock, Sparkle, Lightning, Package, ShieldCheck, Leaf, ChatCircle } from '@phosphor-icons/react';
import { motion, useScroll, useTransform } from 'framer-motion';
import ContactSection from '@/components/ContactSection';
import PremiumFooter from '@/components/PremiumFooter';

const NOVA_LOGO = "https://customer-assets.emergentagent.com/job_nova-fit-meals/artifacts/8r5uywp4_Nova%20Foods.png";

const FEATURED_MEALS = [
  { name: 'Power Bowl', category: 'Muscle Gain', calories: 650, protein: 40, image: '/meals/power-bowl.png' },
  { name: 'Lean Recovery Bowl', category: 'Weight Loss', calories: 350, protein: 25, image: '/meals/lean-recovery-bowl.png' },
  { name: 'Mango Protein Yogurt', category: 'Post-Workout', calories: 500, protein: 25, image: '/meals/mango-protein-yogurt.png' },
  { name: 'Paneer Power Bowl', category: 'Muscle Gain', calories: 600, protein: 35, image: '/meals/paneer-power-bowl.png' },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogin = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = encodeURIComponent(window.location.origin + '/auth/google/callback');
    const scope = encodeURIComponent('openid email profile');
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&access_type=offline&prompt=consent`;
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] overflow-x-hidden">
      {/* Header */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-2xl border-b border-stone-100 shadow-sm' : 'bg-transparent'}`} data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 font-heading text-2xl font-semibold text-[#1A4331]">
            <img src={NOVA_LOGO} alt="Nova Foods" className="h-10 w-10 rounded-lg object-cover" />
            Nova Foods
          </div>

          <nav className="hidden md:flex items-center gap-8">
            <a href="#services" className="text-stone-600 hover:text-[#1A4331] font-medium transition-colors text-sm">Services</a>
            <a href="#menu" className="text-stone-600 hover:text-[#1A4331] font-medium transition-colors text-sm">Menu</a>
            <button onClick={() => navigate('/wellness')} className="text-stone-600 hover:text-[#1A4331] font-medium transition-colors text-sm" data-testid="nav-wellness">Wellness</button>
            <button onClick={() => navigate('/bulk-orders')} className="text-stone-600 hover:text-[#1A4331] font-medium transition-colors text-sm" data-testid="nav-bulk-orders">Bulk Orders</button>
            <a href="#how" className="text-stone-600 hover:text-[#1A4331] font-medium transition-colors text-sm">How it Works</a>
          </nav>

          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/login')} className="hidden sm:block text-stone-700 hover:text-[#1A4331] font-medium text-sm">
              Login
            </button>
            <button
              onClick={() => navigate('/plans')}
              className="bg-[#1A4331] text-white px-5 py-2.5 rounded-full font-medium hover:bg-[#2A5A44] transition-all duration-200 text-sm flex items-center gap-1.5 shadow-sm hover:shadow-md"
              data-testid="get-started-btn"
            >
              Get Started
              <ArrowRight size={16} weight="bold" />
            </button>
          </div>
        </div>
      </header>

      {/* HERO SECTION */}
      <section className="relative pt-8 pb-24 md:pt-16 md:pb-32 px-6" data-testid="hero-section">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* Left column */}
          <div className="relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-[#1A4331]/10 text-[#1A4331] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.15em] mb-6"
            >
              <Sparkle size={14} weight="fill" /> Fresh Meals, Everyday
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold tracking-tight text-[#1C1917] font-heading leading-[1.05] mb-6"
              data-testid="hero-heading"
            >
              Meals Designed for<br />
              <span className="text-[#1A4331] relative">
                Your Goals
                <svg className="absolute -bottom-2 left-0 w-full" height="12" viewBox="0 0 200 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 8C40 4 80 4 120 6C160 8 190 8 198 4" stroke="#C05C3D" strokeWidth="4" strokeLinecap="round"/>
                </svg>
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg text-stone-600 mb-8 max-w-xl leading-relaxed"
            >
              High-protein post-workout meals, expert-guided nutrition, and corporate catering — all crafted from fresh, seasonal ingredients.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-3 mb-10"
            >
              <button
                onClick={() => navigate('/plans')}
                className="bg-[#1A4331] text-white px-7 py-4 rounded-full font-medium hover:bg-[#2A5A44] transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl group"
                data-testid="hero-cta-button"
              >
                Start Fitness Plan
                <ArrowRight size={20} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => navigate('/corporate-catering')}
                className="bg-white border border-stone-200 text-stone-900 px-7 py-4 rounded-full font-medium hover:bg-stone-50 transition-all duration-200 flex items-center justify-center gap-2 shadow-sm hover:shadow"
                data-testid="corporate-hero-btn"
              >
                Corporate Catering
                <ArrowUpRight size={20} weight="bold" />
              </button>
            </motion.div>

            {/* Trust indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex items-center gap-6 text-sm text-stone-600"
            >
              <div className="flex -space-x-2">
                {['#FBBF24', '#34D399', '#C05C3D', '#1A4331'].map((c, i) => (
                  <div key={c} className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: c }}>{['R', 'S', 'A', 'V'][i]}</div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map((s) => <Star key={s} size={14} weight="fill" className="text-[#FBBF24]" />)}
                  <span className="ml-1 font-bold text-stone-800">4.9</span>
                </div>
                <p className="text-xs text-stone-500">Trusted by fitness lovers</p>
              </div>
            </motion.div>
          </div>

          {/* Right column - Hero visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            {/* Decorative blobs */}
            <div className="absolute -top-8 -right-8 w-72 h-72 bg-[#C05C3D]/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-8 -left-8 w-72 h-72 bg-[#1A4331]/10 rounded-full blur-3xl"></div>

            {/* Main image with floating cards */}
            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-[#1A4331] to-[#2A5A44] rounded-[3rem] p-3 shadow-2xl">
                <img
                  src="/meals/paneer-power-bowl.png"
                  alt="Signature Meal"
                  className="w-full h-full object-cover rounded-[2.5rem]"
                />
              </div>

              {/* Floating card - Nutrition */}
              <motion.div
                initial={{ opacity: 0, x: -30, y: -20 }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="absolute -top-6 -left-6 md:-left-8 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 border border-stone-100"
              >
                <div className="w-12 h-12 bg-[#34D399]/15 rounded-xl flex items-center justify-center">
                  <Barbell size={24} weight="duotone" className="text-[#34D399]" />
                </div>
                <div>
                  <p className="text-xs text-stone-500">Protein</p>
                  <p className="text-lg font-bold text-[#1C1917]">40g <span className="text-xs text-stone-400 font-normal">avg</span></p>
                </div>
              </motion.div>

              {/* Floating card - Calories */}
              <motion.div
                initial={{ opacity: 0, x: 30, y: 20 }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="absolute -bottom-6 -right-6 md:-right-8 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3 border border-stone-100"
              >
                <div className="w-12 h-12 bg-[#C05C3D]/15 rounded-xl flex items-center justify-center">
                  <Fire size={24} weight="duotone" className="text-[#C05C3D]" />
                </div>
                <div>
                  <p className="text-xs text-stone-500">Calorie tracked</p>
                  <p className="text-lg font-bold text-[#1C1917]">Every meal</p>
                </div>
              </motion.div>

              {/* Floating card - AI */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1 }}
                className="absolute top-1/2 -right-4 md:-right-10 bg-white rounded-2xl shadow-xl p-3 border border-stone-100 hidden md:flex items-center gap-2"
              >
                <Brain size={20} weight="duotone" className="text-[#1A4331]" />
                <span className="text-xs font-bold text-stone-800">Expert Guided</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="border-y border-stone-100 bg-white py-8 px-6" data-testid="stats-strip">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { value: '500+', label: 'Meals Delivered' },
            { value: '95%', label: 'Repeat Customers' },
            { value: '24h', label: 'Advance Booking' },
            { value: '100%', label: 'Fresh Daily' },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.05 * i }}
              className="text-center md:text-left"
            >
              <p className="text-3xl sm:text-4xl font-bold text-[#1A4331] font-heading">{stat.value}</p>
              <p className="text-xs sm:text-sm text-stone-500 uppercase tracking-wider mt-1">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section id="services" className="py-20 md:py-28 px-6" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C05C3D] mb-3">What we offer</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1C1917] font-heading mb-4">
              Three ways to eat better
            </h2>
            <p className="text-lg text-stone-600 leading-relaxed">
              Chasing fitness goals, feeding a corporate event, or hosting a house party — we&apos;ve got you covered.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-6 gap-6">
            {/* Fitness Card - Large */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              onClick={() => navigate('/plans')}
              className="lg:col-span-4 relative bg-gradient-to-br from-[#1A4331] to-[#2A5A44] text-white rounded-[2rem] p-8 md:p-10 overflow-hidden cursor-pointer group hover:shadow-2xl transition-all duration-500"
              data-testid="fitness-service-card"
            >
              <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#34D399]/20 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-16 -left-16 w-72 h-72 bg-[#C05C3D]/10 rounded-full blur-3xl"></div>

              <div className="relative z-10">
                <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center mb-6">
                  <HeartStraight size={32} weight="duotone" className="text-[#34D399]" />
                </div>
                <h3 className="text-3xl sm:text-4xl font-medium mb-4 font-heading">Fitness Subscriptions</h3>
                <p className="text-stone-200 leading-relaxed mb-6 max-w-md">
                  Weekly & monthly meal plans built around your fitness goals. High-protein, tracked macros, delivered to your doorstep.
                </p>

                <div className="grid grid-cols-2 gap-3 max-w-sm mb-8">
                  {[
                    { icon: Fire, label: 'Weight Loss', color: '#C05C3D' },
                    { icon: Barbell, label: 'Muscle Gain', color: '#34D399' },
                  ].map((item) => (
                    <div key={item.label} className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/10">
                      <item.icon size={22} weight="duotone" style={{ color: item.color }} className="mb-2" />
                      <p className="text-sm font-medium">{item.label}</p>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <button className="bg-[#34D399] text-[#1A4331] px-6 py-3 rounded-full font-bold hover:bg-[#10B981] transition-all flex items-center gap-2" data-testid="fitness-cta">
                    View Plans
                    <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                  </button>
                  <p className="text-sm text-stone-300">Starting at <span className="font-bold text-white">₹1,350/week</span></p>
                </div>
              </div>
            </motion.div>

            {/* Corporate Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              onClick={() => navigate('/corporate-catering')}
              className="lg:col-span-2 relative bg-gradient-to-br from-[#FBBF24] via-[#F59E0B] to-[#EA580C] text-[#78350F] rounded-[2rem] p-8 md:p-10 overflow-hidden cursor-pointer group hover:shadow-2xl hover:-translate-y-1 transition-all duration-500"
              data-testid="corporate-service-card"
            >
              {/* decorative orbs */}
              <div className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 bg-white/25 rounded-full blur-2xl"></div>
              <div className="pointer-events-none absolute -bottom-20 -left-16 w-48 h-48 bg-[#EA580C]/40 rounded-full blur-3xl"></div>

              <div className="relative z-10">
                <div className="w-14 h-14 rounded-2xl bg-white/40 backdrop-blur-sm border border-white/60 flex items-center justify-center mb-6">
                  <Buildings size={32} weight="duotone" className="text-[#78350F]" />
                </div>
                <h3 className="text-3xl sm:text-4xl font-medium text-[#78350F] mb-4 font-heading">Corporate Catering</h3>
                <p className="text-[#78350F]/80 leading-relaxed mb-6">
                  Fresh meals for office events, meetings, birthdays, and workshops. 20-500 meals, delivered on time.
                </p>

                <ul className="space-y-2 mb-8">
                  {['32+ menu items', 'GST invoice included', '3-day advance booking'].map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-[#78350F] font-medium">
                      <CheckCircle size={16} weight="fill" className="text-[#78350F]" />
                      {item}
                    </li>
                  ))}
                </ul>

                <button className="bg-[#78350F] text-[#FBBF24] px-6 py-3 rounded-full font-bold hover:bg-[#5c2809] transition-all flex items-center gap-2 shadow-lg shadow-[#78350F]/20" data-testid="corporate-cta">
                  Order Now
                  <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </motion.div>

            {/* Bulk Orders Card — full width row 2 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              onClick={() => navigate('/bulk-orders')}
              className="lg:col-span-6 relative bg-gradient-to-br from-[#3B1F5C] via-[#5B21B6] to-[#7C3AED] text-white rounded-[2rem] p-8 md:p-10 overflow-hidden cursor-pointer group hover:shadow-2xl hover:-translate-y-1 transition-all duration-500"
              data-testid="bulk-service-card"
            >
              <div className="pointer-events-none absolute -top-20 -right-20 w-80 h-80 bg-[#F472B6]/25 rounded-full blur-3xl"></div>
              <div className="pointer-events-none absolute -bottom-24 -left-16 w-72 h-72 bg-[#FBBF24]/15 rounded-full blur-3xl"></div>
              {/* subtle dot grid */}
              <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '20px 20px' }} />

              <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 text-[#FBBF24] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.15em] mb-5">
                    <Sparkle size={14} weight="fill" /> New
                  </div>
                  <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur border border-white/15 flex items-center justify-center mb-6">
                    <Package size={32} weight="duotone" className="text-[#F472B6]" />
                  </div>
                  <h3 className="text-3xl sm:text-4xl font-medium mb-4 font-heading">House Parties &amp; Events</h3>
                  <p className="text-stone-200 leading-relaxed mb-6 max-w-lg">
                    Hosting a birthday, housewarming, festival gathering or family reunion? Get the same chef-crafted catering menu — for your home.
                  </p>
                  <button className="bg-[#FBBF24] text-[#3B1F5C] px-6 py-3 rounded-full font-bold hover:bg-white transition-all flex items-center gap-2 shadow-lg shadow-black/20" data-testid="bulk-cta">
                    Start Bulk Order
                    <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: ForkKnife, label: '32+ dishes' },
                    { icon: ShieldCheck, label: 'FSSAI Certified' },
                    { icon: Clock, label: '3-day notice' },
                    { icon: Fire, label: 'Fresh & hot' },
                  ].map((item) => (
                    <div key={item.label} className="bg-white/10 backdrop-blur border border-white/10 rounded-2xl p-4 flex items-center gap-2.5">
                      <item.icon size={20} weight="duotone" className="text-[#F472B6] flex-shrink-0" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FEATURED MEALS */}
      <section id="menu" className="py-20 md:py-24 px-6 bg-white" data-testid="menu-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div className="max-w-xl">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C05C3D] mb-3">Fresh from the kitchen</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1C1917] font-heading">Meals worth showing off</h2>
            </div>
            <button onClick={() => navigate('/plans')} className="text-[#1A4331] hover:text-[#C05C3D] font-medium flex items-center gap-2 group">
              See full menu <ArrowUpRight size={18} weight="bold" className="group-hover:rotate-45 transition-transform" />
            </button>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {FEATURED_MEALS.map((meal, i) => (
              <motion.div
                key={meal.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * i }}
                whileHover={{ y: -8 }}
                className="group cursor-pointer"
                onClick={() => navigate('/plans')}
                data-testid={`featured-meal-${i}`}
              >
                <div className="aspect-square rounded-2xl overflow-hidden bg-stone-100 mb-4 relative">
                  <img src={meal.image} alt={meal.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute top-3 left-3 bg-white/95 backdrop-blur px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-[#1A4331]">{meal.category}</div>
                </div>
                <h3 className="font-medium text-[#1C1917] mb-1 group-hover:text-[#1A4331] transition-colors">{meal.name}</h3>
                <div className="flex items-center gap-3 text-xs text-stone-500">
                  <span className="flex items-center gap-1"><Fire size={12} weight="duotone" /> {meal.calories} cal</span>
                  <span className="flex items-center gap-1"><Barbell size={12} weight="duotone" /> {meal.protein}g</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-20 md:py-28 px-6" data-testid="how-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C05C3D] mb-3">Simple as 1-2-3</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1C1917] font-heading mb-4">How it works</h2>
            <p className="text-lg text-stone-600 leading-relaxed">From selection to delivery, we&apos;ve kept it stupid simple.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { step: '01', icon: ForkKnife, title: 'Pick your plan', desc: 'Choose Weight Loss or Muscle Gain, or order corporate catering for your event.', color: '#1A4331' },
              { step: '02', icon: Package, title: 'We prepare fresh', desc: 'Meals are cooked daily in our cloud kitchen with fresh, seasonal ingredients.', color: '#C05C3D' },
              { step: '03', icon: Lightning, title: 'Delivered to you', desc: 'On-time delivery, tracked macros, and a status you can check anytime.', color: '#34D399' },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.15 * i }}
                className="relative bg-white border border-stone-100 rounded-3xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
              >
                <p className="text-6xl font-bold font-heading text-stone-100 mb-4">{item.step}</p>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${item.color}15`, color: item.color }}>
                  <item.icon size={28} weight="duotone" />
                </div>
                <h3 className="text-xl font-medium text-[#1C1917] mb-3 font-heading">{item.title}</h3>
                <p className="text-stone-600 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WELLNESS MARKETPLACE SPOTLIGHT */}
      <section id="wellness-spotlight" className="py-20 md:py-28 px-6" data-testid="wellness-spotlight">
        <div className="max-w-7xl mx-auto">
          <div className="relative bg-[#0F2A20] text-white rounded-[2.5rem] overflow-hidden shadow-2xl">
            {/* Decorative gradient blobs */}
            <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#34D399]/20 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#EC4899]/10 rounded-full blur-3xl"></div>
            <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-[#7C3AED]/10 rounded-full blur-3xl"></div>

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 p-8 md:p-12 lg:p-16 items-center">
              {/* LEFT — copy + CTAs */}
              <div>
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 text-[#34D399] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.15em] mb-6">
                  <ShieldCheck size={14} weight="fill" /> Nova Wellness · New
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white font-heading mb-6 leading-tight">
                  Real experts. <em className="text-[#34D399] not-italic">Real results.</em>
                </h2>
                <p className="text-lg text-stone-300 mb-8 leading-relaxed">
                  Book verified nutritionists, coaches, therapists and yoga instructors — all credential-checked by our team. Chat, schedule, and stay on track with your goals.
                </p>

                {/* Trust markers */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                  {[
                    { icon: ShieldCheck, label: 'Credential Verified' },
                    { icon: HeartStraight, label: 'Human, Not AI' },
                    { icon: Lightning, label: 'Book Instantly' },
                    { icon: ChatCircle, label: 'In-App Messaging' },
                  ].map((item) => (
                    <div key={item.label} className="bg-white/5 backdrop-blur border border-white/10 rounded-xl px-4 py-3 flex items-center gap-2.5">
                      <item.icon size={18} weight="duotone" className="text-[#34D399]" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => navigate('/wellness/find')}
                    className="bg-[#34D399] text-[#0F2A20] px-7 py-3.5 rounded-full font-bold hover:bg-[#10B981] transition-all flex items-center gap-2"
                    data-testid="wellness-spotlight-cta-browse"
                  >
                    Browse Professionals
                    <ArrowRight size={18} weight="bold" />
                  </button>
                  <button
                    onClick={() => navigate('/wellness/join')}
                    className="bg-white/10 backdrop-blur border border-white/20 text-white px-7 py-3.5 rounded-full font-bold hover:bg-white/20 transition-all"
                    data-testid="wellness-spotlight-cta-join"
                  >
                    Join as a Pro
                  </button>
                </div>
              </div>

              {/* RIGHT — category cards */}
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-stone-400 font-bold mb-4">Explore by Category</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { id: 'nutrition', name: 'Nutrition', color: '#10B981', desc: 'Diet plans & meal coaching' },
                    { id: 'fitness', name: 'Fitness', color: '#EA580C', desc: 'Trainers & coaches' },
                    { id: 'yoga', name: 'Yoga & Mind', color: '#7C3AED', desc: 'Yoga & meditation' },
                    { id: 'mental', name: 'Mental Wellness', color: '#EC4899', desc: 'Therapy & counselling' },
                    { id: 'recovery', name: 'Recovery', color: '#0EA5E9', desc: 'Physio & mobility' },
                    { id: 'lifestyle', name: 'Lifestyle', color: '#F59E0B', desc: 'Sleep & habits' },
                  ].map((cat, i) => (
                    <motion.button
                      key={cat.id}
                      onClick={() => navigate(`/wellness/find?category=${cat.id}`)}
                      initial={{ opacity: 0, y: 12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.15 + i * 0.06 }}
                      whileHover={{ y: -3, scale: 1.02 }}
                      className="text-left bg-white/5 backdrop-blur border border-white/10 hover:border-white/25 rounded-2xl p-4 transition-all group"
                      data-testid={`wellness-cat-${cat.id}`}
                    >
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center mb-3 shadow-inner"
                        style={{ backgroundColor: `${cat.color}22`, boxShadow: `inset 0 0 0 1px ${cat.color}55` }}
                      >
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cat.color }} />
                      </div>
                      <p className="text-sm font-semibold text-white mb-0.5">{cat.name}</p>
                      <p className="text-[11px] text-stone-400 leading-tight">{cat.desc}</p>
                    </motion.button>
                  ))}
                </div>

                <button
                  onClick={() => navigate('/wellness')}
                  className="w-full mt-4 bg-white/5 backdrop-blur border border-white/10 hover:bg-white/10 text-white text-sm font-semibold py-3 rounded-2xl transition-all flex items-center justify-center gap-2"
                  data-testid="wellness-spotlight-see-all"
                >
                  See all wellness services
                  <ArrowRight size={14} weight="bold" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 md:py-28 px-6 bg-white" data-testid="testimonials">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C05C3D] mb-3">Loved by many</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1C1917] font-heading">What our customers say</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: 'Rahul Sharma', role: 'Fitness Enthusiast', quote: 'The muscle gain plan hit perfectly. High protein, delicious meals, and no meal prep hassle.', color: '#1A4331' },
              { name: 'Priya Patel', role: 'HR Manager', quote: 'Ordered catering for our office event. 100 meals delivered right on time — everyone loved it!', color: '#C05C3D' },
              { name: 'Arjun Verma', role: 'Athlete', quote: 'My Nova Wellness nutritionist mapped my macros perfectly. Lost 4kg in 6 weeks eating actual food.', color: '#34D399' },
            ].map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * i }}
                className="bg-[#FAFAF7] border border-stone-100 rounded-3xl p-8"
              >
                <div className="flex gap-1 mb-4">
                  {[1,2,3,4,5].map((s) => <Star key={s} size={16} weight="fill" className="text-[#FBBF24]" />)}
                </div>
                <p className="text-stone-700 leading-relaxed mb-6">&ldquo;{t.quote}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: t.color }}>
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-[#1C1917]">{t.name}</p>
                    <p className="text-xs text-stone-500">{t.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 md:py-24 px-6" data-testid="final-cta">
        <div className="max-w-5xl mx-auto text-center bg-gradient-to-br from-[#FDF6E3] to-[#F7F5F0] rounded-[2.5rem] p-8 md:p-16 border border-stone-100 shadow-sm">
          <Leaf size={40} weight="duotone" className="text-[#1A4331] mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1C1917] font-heading mb-4">
            Ready to eat smarter?
          </h2>
          <p className="text-lg text-stone-600 leading-relaxed mb-8 max-w-2xl mx-auto">
            Join hundreds of fitness lovers and corporate teams who trust Nova Foods for their meals.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center">
            <button onClick={() => navigate('/plans')} className="bg-[#1A4331] text-white px-8 py-4 rounded-full font-medium hover:bg-[#2A5A44] transition-all shadow-lg flex items-center justify-center gap-2">
              Start Fitness Plan <ArrowRight size={20} weight="bold" />
            </button>
            <button onClick={() => navigate('/corporate-catering')} className="bg-gradient-to-br from-[#FBBF24] to-[#F59E0B] text-[#78350F] px-8 py-4 rounded-full font-bold hover:shadow-2xl hover:shadow-amber-500/25 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 shadow-lg">
              Corporate Catering <ArrowUpRight size={20} weight="bold" />
            </button>
            <button onClick={() => navigate('/bulk-orders')} className="bg-gradient-to-br from-[#5B21B6] to-[#7C3AED] text-white px-8 py-4 rounded-full font-bold hover:shadow-2xl hover:shadow-purple-500/25 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 shadow-lg" data-testid="cta-bulk-orders">
              Bulk / House Party <ArrowUpRight size={20} weight="bold" />
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <ContactSection />
      <PremiumFooter />
    </div>
  );
};

export default LandingPage;
